import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Gift, Ticket, Check, AlertCircle, ArrowRight, User, Sparkles, ServerOff } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslations } from '../context/TranslationsContext';
import { useFitnet } from '../contexts/FitnetContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getHomePath } from '../utils/routes';

const API = process.env.REACT_APP_BACKEND_URL;

const ActivateGiftPassPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td } = useTranslations();
  const { user, isAuthenticated, token } = useAuth();
  const { isAvailable: isFitnetAvailable, loading: fitnetLoading } = useFitnet();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = i18n.language;
  
  const t = (key, fallback) => {
    const cmsValue = td(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  // State
  const [passCode, setPassCode] = useState(searchParams.get('code') || '');
  const [assignTo, setAssignTo] = useState('main');
  const [subaccounts, setSubaccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activatedPass, setActivatedPass] = useState(null);

  // Fetch subaccounts if logged in
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchSubaccounts();
    }
  }, [isAuthenticated, token]);

  const fetchSubaccounts = async () => {
    try {
      const response = await axios.get(`${API}/api/subaccounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubaccounts(response.data || []);
    } catch (err) {
      console.error('Failed to fetch subaccounts:', err);
    }
  };

  // Build list of people to assign pass to
  const allPeople = [
    { 
      id: 'main', 
      name: user ? `${user.firstName} ${user.lastName}` : t('common.me', 'Me'),
      label: t('common.mainAccount', '(Main account)')
    },
    ...(subaccounts || []).map(sub => ({
      id: sub.subaccount_id || sub.id,
      name: `${sub.firstName} ${sub.lastName}`,
      label: ''
    }))
  ];

  const handleActivate = async () => {
    if (!passCode.trim()) {
      setError(t('errors.passCodeRequired', 'Please enter a pass code'));
      return;
    }

    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/activate-pass?code=${encodeURIComponent(passCode)}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API}/api/passes/activate`,
        {
          passCode: passCode.trim().toUpperCase(),
          assignToSubaccountId: assignTo !== 'main' ? assignTo : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      setActivatedPass(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || t('errors.activationFailed', 'Failed to activate pass');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Success view
  if (success) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6" data-testid="activate-pass-success">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
              <Check className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl font-['Syne'] font-bold mb-4">
              {t('passes.activationSuccess', 'Pass Activated!')}
            </h1>
            
            <p className="text-muted-foreground mb-6">
              {t('passes.activationSuccessDescription', 'Your gift pass has been successfully activated and assigned to your account.')}
            </p>

            {activatedPass?.passName && (
              <div className="bg-primary/10 rounded-xl p-4 mb-6">
                <p className="text-sm text-muted-foreground mb-1">{t('passes.activatedPass', 'Activated pass')}</p>
                <p className="text-xl font-bold text-primary">{activatedPass.passName}</p>
              </div>
            )}

            <Link
              to="/account?tab=passes"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-medium hover:bg-primary/90 transition-colors"
            >
              {t('passes.viewMyPasses', 'View My Passes')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show loading while checking FitNet
  if (fitnetLoading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t('common.loading', 'Ładowanie...')}</p>
        </div>
      </div>
    );
  }

  // Show unavailable message when FitNet is down
  if (!isFitnetAvailable) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg"
        >
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/20 flex items-center justify-center">
              <ServerOff className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-['Syne'] font-bold mb-4">{t('maintenance.activationUnavailable', 'Aktywacja karnetu jest tymczasowo niedostępna')}</h1>
            <p className="text-muted-foreground mb-6">
              {t('maintenance.description', 'System rezerwacji jest chwilowo wyłączony. Przepraszamy za utrudnienia.')}
            </p>
            <Link 
              to={getHomePath(lang)} 
              className="btn-primary inline-block"
            >
              {t('maintenance.backToHome', 'Wróć do strony głównej')}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6" data-testid="activate-gift-pass-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-6">
            <Gift className="w-8 h-8 text-primary" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-['Syne'] font-bold mb-4">
            {t('passes.activateGiftPass', 'Activate Gift Pass')}
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-md mx-auto">
            {t('passes.activateGiftPassDescription', 'Received a pass as a gift? Enter the code below to add it to your account.')}
          </p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-8"
        >
          {/* Info banner */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10 mb-8">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">
                {t('passes.giftPassInfo', 'How does it work?')}
              </p>
              <p>
                {t('passes.giftPassInfoDescription', 'When someone purchases a pass as a gift, they receive a unique code. Enter that code here to claim your pass and start using it.')}
              </p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-500">{error}</p>
            </motion.div>
          )}

          {/* Form */}
          <div className="space-y-6">
            {/* Pass Code Input */}
            <div>
              <Label className="text-base font-medium mb-2 block">
                {t('passes.passCode', 'Pass Code')}
              </Label>
              <Input
                value={passCode}
                onChange={(e) => setPassCode(e.target.value.toUpperCase())}
                placeholder="AVE-XXXX-XXXX-XXXX"
                className="input-glass text-lg font-mono tracking-wider uppercase h-14"
                data-testid="pass-code-input"
              />
              <p className="text-sm text-muted-foreground mt-2">
                {t('passes.passCodeHint', 'The code was sent to the gift buyer in an email confirmation.')}
              </p>
            </div>

            {/* Assign To - only if logged in */}
            {isAuthenticated && subaccounts.length > 0 && (
              <div>
                <Label className="text-base font-medium mb-2 block">
                  {t('account.assignTo', 'Assign to')}
                </Label>
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger className="input-glass h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allPeople.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {person.name} {person.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleActivate}
              disabled={loading || !passCode.trim()}
              className="w-full py-4 rounded-xl bg-primary text-white font-semibold text-lg flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="activate-button"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.processing', 'Processing...')}
                </>
              ) : (
                <>
                  <Ticket className="w-5 h-5" />
                  {isAuthenticated 
                    ? t('passes.activatePass', 'Activate Pass')
                    : t('passes.loginToActivate', 'Login to Activate')
                  }
                </>
              )}
            </button>

            {/* Login hint */}
            {!isAuthenticated && (
              <p className="text-center text-sm text-muted-foreground">
                {t('passes.needAccountToActivate', "You need to be logged in to activate a pass.")}
                {' '}
                <Link to="/login" className="text-primary hover:underline">
                  {t('auth.logIn', 'Log in')}
                </Link>
                {' '}{t('common.or', 'or')}{' '}
                <Link to="/register" className="text-primary hover:underline">
                  {t('auth.signUp', 'Sign up')}
                </Link>
              </p>
            )}
          </div>
        </motion.div>

        {/* Help section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 text-center"
        >
          <p className="text-muted-foreground">
            {t('passes.havingTrouble', 'Having trouble?')}{' '}
            <Link to="/contact" className="text-primary hover:underline">
              {t('common.contactUs', 'Contact us')}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ActivateGiftPassPage;
